type BackgroundColor = {
  backgroundColor: string;
};

type FontFamily = {
  fontFamily: string;
};

type BorderRadius = {
  borderRadius: number;
};

type BorderWidth = {
  borderWidth: number;
};

type Shadow = {
  shadowColor?: string;
  shadowOffset: {
    width: number;
    height: number;
  };
  shadowRadius?: number;
  shadowOpacity?: number;
};

type Color = {
  color: string;
};

type FontSize = {
  fontSize: number;
};

type Gradient = string[];

export type SkillzTheme = {
  start: 'skillz';
  colors?: {
    backgroundViewColor?: string;
  };
  sheetStyles: {
    viewPrimaryColor: BackgroundColor;
    viewSecondaryColor: BackgroundColor;
    viewTertiaryColor: BackgroundColor;
    viewHighlightColor: BackgroundColor;
    statusBarViewBackgroundColor: BackgroundColor;
    viewDividerPrimaryColor: BackgroundColor;
    viewDividerColorTwo: BackgroundColor;
    cellBackgroundColor: BackgroundColor;
    cellAlternateBackgroundColor: BackgroundColor;
    playerActivityIndicatorColor: BackgroundColor;
    progressBarColor: BackgroundColor;
    viewCornerRadiusPrimary: BorderRadius;
    viewCornerRadiusSecondary: BorderRadius;
    viewCornerRadiusTertiary: BorderRadius;
    viewBorderWidthPrimary: BorderWidth;
    viewBorderWidthSecondary: BorderWidth;
    viewBorderWidthTertiary: BorderWidth;
    viewBoxShadowPrimary: Shadow;
    fontPrimary: FontFamily;
    fontPrimaryItalic: FontFamily;
    fontSecondary: FontFamily;
    fontRegular: FontFamily;
    fontMedium: FontFamily;
    fontBold: FontFamily;
    fontBoldItalic: FontFamily;
    fontDigital: FontFamily;
    textColorPrimary: Color;
    textColorSecondary: Color;
    textColorTertiary: Color;
    textColorFour: Color;
    textColorError: Color;
    textColorCash: Color;
    textColorZ: Color;
    textColorTicketz: Color;
    textColorLink: Color;
    textColorMedals: Color;
    textColorDepositTile: Color;
    textColorMatchSelectPrize: Color;
    textColorSideMenu: Color;
    textShadowOne: Shadow;
    textShadowTwo: Shadow;
    textShadowThree: Shadow;
    primaryRibbonTextColor: Color;
    fontSizeExtraLarge: FontSize;
    fontSizeVeryLarge: FontSize;
    fontSizeLarge: FontSize;
    fontSizeMedium: FontSize;
    fontSizeH1: FontSize;
    fontSizeH2: FontSize;
    fontSizeH3: FontSize;
    fontSizeH4: FontSize;
    fontSizeH5: FontSize;
    fontSizeH6: FontSize;
    btnTextColorOne: Color;
    btnTextColorTwo: Color;
    btnTextColorThree: Color;
    featuredMatchBarColor: BackgroundColor;
    tabHighlightColor: Color;
    checkMarkColor: BackgroundColor;
    highlightTurnBasedWinColor: Color;
    giftBoxHighlightColor: Color;
  };
  gradients: {
    prizeViewLiveEventGradient: Gradient;
    prizeViewCashGradient: Gradient;
    prizeViewZGradient: Gradient;
    prizeViewTicketzGradient: Gradient;
    bracketPrizeViewLiveEventGradient: Gradient;
    bracketPrizeViewCashGradient: Gradient;
    bracketPrizeViewZGradient: Gradient;
    btnGradientOne: Gradient;
    btnGradientTwo: Gradient;
    btnGradientThree: Gradient;
    btnGradientStore: Gradient;
    playButtonGradient: Gradient;
    textTicketzGradient: Gradient;
    textCashGradient: Gradient;
    textZGradient: Gradient;
    textNeutralGradient: Gradient;
    overlayEntryContainerGradient: Gradient;
    backgroundViewGradient: Gradient;
    sideMenuHighlightedGradient: Gradient;
    cashIconGradient: Gradient;
    practiceCurrencyIconGradient: Gradient;
    profileIconGradient: Gradient;
    progressViewDarkGradient: Gradient;
    navigationBarGradient: Gradient;
    selectedCellBackgroundGradient: Gradient;
    pointsPrizeGradient: Gradient;
    tauntImageGradient: Gradient;
    excitingBannerGradient: Gradient;
    excitingHighlightGradient: Gradient;
    primaryRibbonGradient: Gradient;
  };
  enums: {
    sidePanelBlurStyle: number;
    statusBarContentStyle: number;
  };
  opacityLevels: {
    opacityPrimary: number;
    opacitySecondary: number;
    opacityTertiary: number;
    opacityNone: number;
  };
  currencyValues: {
    practiceCurrencyString: string;
    practiceCurrencySimpleIconURI: string;
    practiceCurrencyFullIconLargeURI: string;
  };
  images: {
    backgroundImage: string;
    prizeViewZImage: string | null;
    prizeViewCashImage: string | null;
    prizeViewLiveEventImage: string | null;
    prizeViewTicketzImage: string | null;
    bracketPrizeViewZImage: string | null;
    bracketPrizeViewCashImage: string | null;
    bracketPrizeViewLiveEventImage: string | null;
    excitingBannerImage: string | null;
  };
  version: {
    SkillzOTAVersion: string | null;
  };
  end: 'skillz';
};

const theme: SkillzTheme = {
  start: 'skillz',
  sheetStyles: {
    viewPrimaryColor: {
      backgroundColor: '#2B2F4E',
    },
    viewSecondaryColor: {
      backgroundColor: 'rgba(255,255,255,.95)',
    },
    viewTertiaryColor: {
      backgroundColor: 'rgba(255,255,255,.9)',
    },
    viewHighlightColor: {
      backgroundColor: 'rgba(144, 183, 237, .7)',
    },
    statusBarViewBackgroundColor: {
      backgroundColor: '#F9F9F9',
    },
    viewDividerPrimaryColor: {
      backgroundColor: 'rgba(255, 255, 255, .15)',
    },
    viewDividerColorTwo: {
      backgroundColor: '#bfc0c1',
    },
    cellBackgroundColor: {
      backgroundColor: 'rgba(255,255,255,.95)',
    },
    cellAlternateBackgroundColor: {
      backgroundColor: '#f6f6f6',
    },
    playerActivityIndicatorColor: {
      backgroundColor: '#6AC141',
    },
    progressBarColor: {
      backgroundColor: '#3f608f',
    },
    viewCornerRadiusPrimary: {
      borderRadius: 5,
    },
    viewCornerRadiusSecondary: {
      borderRadius: 4,
    },
    viewCornerRadiusTertiary: {
      borderRadius: 2,
    },
    viewBorderWidthPrimary: {
      borderWidth: 3,
    },
    viewBorderWidthSecondary: {
      borderWidth: 1.5,
    },
    viewBorderWidthTertiary: {
      borderWidth: 1,
    },
    viewBoxShadowPrimary: {
      shadowColor: '#000000',
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowRadius: 1,
      shadowOpacity: 0.4,
    },
    fontPrimary: {
      fontFamily: 'HelveticaNeue-Light',
    },
    fontPrimaryItalic: {
      fontFamily: 'HelveticaNeue-LightItalic',
    },
    fontSecondary: {
      fontFamily: 'HelveticaNeue-CondensedBlack',
    },
    fontRegular: {
      fontFamily: 'HelveticaNeue',
    },
    fontMedium: {
      fontFamily: 'HelveticaNeue-Medium',
    },
    fontBold: {
      fontFamily: 'HelveticaNeue-Bold',
    },
    fontBoldItalic: {
      fontFamily: 'HelveticaNeue-BoldItalic',
    },
    fontDigital: {
      fontFamily: 'Digital-7',
    },
    textColorPrimary: {
      color: '#FFFFFF',
    },
    textColorSecondary: {
      color: '#4A4A4A',
    },
    textColorTertiary: {
      color: '#999999',
    },
    textColorFour: {
      color: '#7a8fac',
    },
    textColorError: {
      color: '#bc2131',
    },
    textColorCash: {
      color: '#79b23f',
    },
    textColorZ: {
      color: '#4990e2',
    },
    textColorTicketz: {
      color: '#e26954',
    },
    textColorLink: {
      color: '#FFFFFF',
    },
    textColorMedals: {
      color: '#317296',
    },
    textColorDepositTile: {
      color: '#FFFFFF',
    },
    textColorMatchSelectPrize: {
      color: '#FFFFFF',
    },
    textColorSideMenu: {
      color: '#FFFFFF',
    },
    textShadowOne: {
      shadowColor: '#000000',
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowRadius: 0,
      shadowOpacity: 0.4,
    },
    textShadowTwo: {
      shadowColor: '#000000',
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowRadius: 0,
      shadowOpacity: 0.3,
    },
    textShadowThree: {
      shadowColor: '#000000',
      shadowOffset: {
        width: 0,
        height: 1,
      },
      shadowRadius: 0,
      shadowOpacity: 0.2,
    },
    primaryRibbonTextColor: {
      color: '#ffffff',
    },
    fontSizeExtraLarge: {
      fontSize: 85,
    },
    fontSizeVeryLarge: {
      fontSize: 58,
    },
    fontSizeLarge: {
      fontSize: 42,
    },
    fontSizeMedium: {
      fontSize: 35,
    },
    fontSizeH1: {
      fontSize: 30,
    },
    fontSizeH2: {
      fontSize: 22,
    },
    fontSizeH3: {
      fontSize: 18,
    },
    fontSizeH4: {
      fontSize: 15,
    },
    fontSizeH5: {
      fontSize: 12,
    },
    fontSizeH6: {
      fontSize: 8,
    },
    btnTextColorOne: {
      color: '#ffffff',
    },
    btnTextColorTwo: {
      color: '#ffffff',
    },
    btnTextColorThree: {
      color: '#ffffff',
    },
    featuredMatchBarColor: {
      backgroundColor: '#f48e30',
    },
    tabHighlightColor: {
      color: '#4990e2',
    },
    checkMarkColor: {
      backgroundColor: '#4fa93f',
    },
    highlightTurnBasedWinColor: {
      color: '#f9eb0d',
    },
    giftBoxHighlightColor: {
      color: '#395b79',
    },
  },
  gradients: {
    prizeViewLiveEventGradient: ['#6ac141', '#35a1a2'],
    prizeViewCashGradient: ['#6ac141', '#36923e'],
    prizeViewZGradient: ['#387aa4', '#387aa4'],
    prizeViewTicketzGradient: ['#f6604d', '#e05745'],
    bracketPrizeViewLiveEventGradient: ['#6ac141', '#35a1a2'],
    bracketPrizeViewCashGradient: ['#6ac141', '#36923e'],
    bracketPrizeViewZGradient: ['#387aa4', '#387aa4'],
    btnGradientOne: ['#ff6702', '#f89c3d'],
    btnGradientTwo: ['#7a8fac', '#7a8fac'],
    btnGradientThree: ['#3C4358', '#3C4358'],
    btnGradientStore: ['#4fb031', '#29782f'],
    playButtonGradient: ['#d76604', '#fd993d'],
    textTicketzGradient: ['#e26954', '#e26954'],
    textCashGradient: ['#79b23f', '#79b23f'],
    textZGradient: ['#4990e2', '#4990e2'],
    textNeutralGradient: ['#FFFFFF', '#FFFFFF'],
    overlayEntryContainerGradient: ['rgba(114,127,144,.95)', 'rgba(141,154,168,.95)'],
    backgroundViewGradient: ['#3F476B', '#6B80A4'],
    sideMenuHighlightedGradient: ['#2a6994', '#63afd6'],
    cashIconGradient: ['#e9ab24', '#ffe6c5', '#fbde5f'],
    practiceCurrencyIconGradient: ['#5d7696', '#6da5ce'],
    profileIconGradient: ['#6ea8d2', '#5c7291'],
    progressViewDarkGradient: ['#343443', '#484858'],
    navigationBarGradient: ['#000000', '#262a38'],
    selectedCellBackgroundGradient: ['#0099c7', '#005174'],
    pointsPrizeGradient: ['#4581b1', '#5592c1', '#629dca'],
    tauntImageGradient: ['#4686ad', '#2f6d98'],
    excitingBannerGradient: ['#2e2dff', '#fc0132'],
    excitingHighlightGradient: ['#3e00cd', '#b709dd'],
    primaryRibbonGradient: ['#350c95', '#4b0cdf', '#3d03c7', '#4b0cdf', '#350c95'],
  },
  enums: {
    sidePanelBlurStyle: 1,
    statusBarContentStyle: 0,
  },
  opacityLevels: {
    opacityPrimary: 0.9,
    opacitySecondary: 0.8,
    opacityTertiary: 0.7,
    opacityNone: 1,
  },
  currencyValues: {
    practiceCurrencyString: 'ⓩ',
    practiceCurrencySimpleIconURI: 'ic_z_black_med_SKZ@2x.png',
    practiceCurrencyFullIconLargeURI: 'ic_practice_icon_large.png',
  },
  images: {
    backgroundImage: '',
    prizeViewZImage: null,
    prizeViewCashImage: null,
    prizeViewLiveEventImage: null,
    prizeViewTicketzImage: null,
    bracketPrizeViewZImage: null,
    bracketPrizeViewCashImage: null,
    bracketPrizeViewLiveEventImage: null,
    excitingBannerImage: null,
  },
  version: {
    SkillzOTAVersion: null,
  },
  end: 'skillz',
};

export default theme;
